import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon, { EcoIconName } from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import { isAdminFromToken, isRgbUserFromToken } from "../../../utils/authToken";
import { RGB_HOME_PAGE } from "../../rgb/routes";
import {
  getDeficitNotificationsApi,
  getDeficitReportExcelApi,
  DeficitNotification
} from "../api/massBalanceApi";
import {
  ESTOQUE_BALANCO_LOJAS_PAGE,
  ESTOQUE_PRODUTOS_PAGE,
  ESTOQUE_OCORRENCIAS_PAGE,
  ESTOQUE_RELATORIOS_PAGE,
  ESTOQUE_ATUAL_PAGE
} from "../routes";

import "./Estoque.css";
import "../../dp/pages/DpEco.css";

type ModuleCard = {
  title: string;
  description: string;
  onClick: () => void;
  icon: EcoIconName;
  requiresAdmin?: boolean;
  /** Vasilhame RGB: ADMIN e supervisores (DP não). */
  requiresRgb?: boolean;
};

export default function EstoquePage() {
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const isAdmin = useMemo(() => isAdminFromToken(token), [token]);
  const isRgbUser = useMemo(() => isRgbUserFromToken(token), [token]);
  const goBack = useGoBack("/");

  const [deficits, setDeficits] = useState<DeficitNotification[]>([]);
  const [loadingDeficits, setLoadingDeficits] = useState(false);
  useEffect(() => {
    if (!token) {
      toast.error("Você precisa estar logado.");
      navigate(LOGINPAGE(), { replace: true });
    }
  }, [token, navigate]);

  const fetchDeficits = async () => {
    if (!token || !isAdmin) return;
    try {
      setLoadingDeficits(true);
      const data = await getDeficitNotificationsApi(token, false);
      setDeficits(data || []);
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoadingDeficits(false);
    }
  };

  useEffect(() => {
    fetchDeficits();
  }, [token, isAdmin]);

  const handleDownloadDeficit = async (id: string) => {
    if (!token) return;
    try {
      toast.info("Gerando relatório...");
      const blob = await getDeficitReportExcelApi(token, id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `relatorio_deficit_${id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      toast.success("Download iniciado!");
      // Reload deficits since downloaded ones are dismissed on backend
      fetchDeficits();
    } catch (err: any) {
      toast.error(err.message || "Erro ao baixar relatório.");
    }
  };

  const sections = useMemo<{ id: string; title: string; modules: ModuleCard[] }[]>(() => {
    const visible = (m: ModuleCard) => (!m.requiresAdmin || isAdmin) && (!m.requiresRgb || isRgbUser);
    return [
      {
        id: "contagem",
        title: "Contagem e controle",
        modules: [
          {
            title: "Balanço de massa",
            description: "Contagem e controle de estoque por loja.",
            onClick: () => navigate(ESTOQUE_BALANCO_LOJAS_PAGE()),
            icon: "scale" as EcoIconName,
          },
          {
            title: "Estoque atual",
            description: "Consultar e corrigir manualmente o estoque atual de cada loja.",
            onClick: () => navigate(ESTOQUE_ATUAL_PAGE()),
            icon: "box" as EcoIconName,
          },
          {
            title: "Ocorrências de estoque",
            description: "Avarias, vencimentos, devoluções e consumos registrados.",
            onClick: () => navigate(ESTOQUE_OCORRENCIAS_PAGE()),
            icon: "alertTriangle" as EcoIconName,
          },
        ].filter(visible),
      },
      {
        id: "vasilhame",
        title: "Vasilhame",
        modules: [
          {
            title: "Vasilhame RGB",
            description: "Refugo, recebimento e devolução de vasilhame retornável por loja.",
            onClick: () => navigate(RGB_HOME_PAGE()),
            icon: "bottle" as EcoIconName,
            requiresRgb: true,
          },
        ].filter(visible),
      },
      {
        id: "cadastros",
        title: "Cadastros e relatórios",
        modules: [
          {
            title: "Produtos",
            description: "Produtos, referências De/Para e rastreamento no balanço.",
            onClick: () => navigate(ESTOQUE_PRODUTOS_PAGE()),
            icon: "layers" as EcoIconName,
            requiresAdmin: true,
          },
          {
            title: "Relatórios",
            description: "Balanços, movimentações de entradas e saídas e notificações de déficit.",
            onClick: () => navigate(ESTOQUE_RELATORIOS_PAGE()),
            icon: "file" as EcoIconName,
            requiresAdmin: true,
          },
        ].filter(visible),
      },
    ].filter((s) => s.modules.length > 0);
  }, [isAdmin, isRgbUser, navigate]);

  if (!token) return null;

  const formatRef = (d: DeficitNotification) =>
    d.massBalance?.referenceDate
      ? new Date(d.massBalance.referenceDate).toLocaleDateString("pt-BR", { timeZone: "UTC" })
      : "";

  return (
    <div className="dpePage">
      <main className="dpePage__main">
        <EcoPageHeader
          crumbs={[{ label: "Início", onClick: () => navigate("/") }, { label: "Estoque" }]}
          title="Estoque"
          subtitle="Balanço de massa, estoque atual, ocorrências, vasilhame, produtos e relatórios."
          onBack={goBack}
          backLabel="Voltar ao início"
        />

        {/* Déficits pendentes: antes ficavam num sino flutuante; agora no topo da página. */}
        {isAdmin && (loadingDeficits || deficits.length > 0) ? (
          <section className="dpeSection" aria-labelledby="est-atencao">
            <h2 id="est-atencao" className="dpeSection__title">
              Atenção: déficits pendentes{deficits.length ? ` (${deficits.length})` : ""}
            </h2>
            {loadingDeficits ? (
              <p className="ecoEmpty">Carregando déficits...</p>
            ) : (
              <div className="dpeAlerts">
                {deficits.map((d) => (
                  <div key={d.id} className="dpeAlert dpeAlert--calm estDeficit">
                    <span className="dpeAlert__dot dpeAlert__dot--bad" aria-hidden="true" />
                    <span className="dpeAlert__text">
                      <span className="dpeAlert__title">{d.store?.nomeFantasia || "Loja"}</span>
                      <span className="dpeAlert__desc">
                        Balanço de {formatRef(d)} com {d.itemCount} {d.itemCount === 1 ? "item" : "itens"} em déficit.
                      </span>
                      <button
                        type="button"
                        className="ecoBtn ecoBtn--sm estDeficit__btn"
                        onClick={() => handleDownloadDeficit(d.massBalanceId)}
                      >
                        <EcoIcon name="download" size={16} />
                        Baixar Excel
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}
            <p className="estDeficit__note">Ao baixar o relatório, o déficit sai desta lista.</p>
          </section>
        ) : null}

        {sections.map((section) => (
          <section key={section.id} className="dpeSection" aria-labelledby={`est-${section.id}`}>
            <h2 id={`est-${section.id}`} className="dpeSection__title">{section.title}</h2>
            <div className="dpeModules">
              {section.modules.map((m) => (
                <button key={m.title} type="button" className="dpeModule" onClick={m.onClick}>
                  <span className="dpeModule__icon">
                    <EcoIcon name={m.icon} size={20} strokeWidth={1.8} />
                  </span>
                  <span className="dpeModule__text">
                    <span className="dpeModule__title">{m.title}</span>
                    <span className="dpeModule__desc">{m.description}</span>
                  </span>
                  <EcoIcon name="chevronRight" className="dpeModule__chevron" />
                </button>
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
